import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Action from '@sharedComponents/Action';
import Loader from '@sharedComponents/Loader';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';

import * as style from './style.module.css';

const ICON_SIZE_BY_SIZE = {
    xs: 'var(--icon-s)',
};

function Button({
    title = '',
    label = '',
    disabled = false,
    theme = 'primary',
    icon = null,
    ghost = false,
    outline = false,
    isLoading = false,
    action = null,
    noPadding = false,
    rounded = false,
    role = 'button',
    size = 'm',
    tabIndex = 0,
    warningIcon = null,
    ...props
}) {
    const [internalLoading, setInternalLoading] = useState(false);

    const wrappedAction = useCallback(() => {
        const result = action();
        if (result instanceof Promise) {
            setInternalLoading(true);
            result.finally(() => setInternalLoading(false));
        }
    }, [action]);

    const effectiveLoading = isLoading || internalLoading;
    const effectiveAction = typeof action === 'function' ? wrappedAction : action;

    return (
        <Action
            {...props}
            action={!disabled && !effectiveLoading ? effectiveAction : null}
            title={title}
            role={role}
            tabIndex={tabIndex}
            aria-disabled={disabled}
            className={cx(style.button, style[theme], {
                [style.isLoading]: effectiveLoading,
                [style.disabled]: disabled,
                [style.ghost]: ghost,
                [style.outline]: outline,
                [style.noPadding]: noPadding,
                [style.rounded]: rounded,
                [style[size]]: size,
            })}
        >
            <div className={cx(style.labelContainer)}>
                {icon && (
                    <FontAwesomeIcon
                        className={cx({ [style.icon]: label })}
                        icon={icon}
                        style={ICON_SIZE_BY_SIZE[size] ? { fontSize: ICON_SIZE_BY_SIZE[size] } : undefined}
                    />
                )}
                {label}
                {warningIcon && <FontAwesomeIcon className={cx({ [style.warningIcon]: label })} icon={warningIcon} />}
            </div>
            <Loader className={cx(style.loading)} />
        </Action>
    );
}

Button.propTypes = {
    label: PropTypes.string,
    disabled: PropTypes.bool,
    theme: PropTypes.oneOf(['primary', 'light', 'inverted', 'danger']),
    icon: PropTypes.object,
    ghost: PropTypes.bool,
    outline: PropTypes.bool,
    isLoading: PropTypes.bool,
    noPadding: PropTypes.bool,
    rounded: PropTypes.bool,
    action: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    role: PropTypes.string,
    tabIndex: PropTypes.number,
};

export default Button;
